<script setup>
import { computed, defineEmits, ref, watch, onMounted, nextTick } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { debounce } from '@chatwoot/utils';
import MultiselectDropdown from '../../../../../shared/components/ui/MultiselectDropdown.vue';

const props = defineProps({
  contacts: {
    type: Array,
    default: () => [],
  },
  selectedContactId: {
    type: [Number, String],
    default: null,
  },
  initialContact: {
    type: Object,
    default: null,
  },
  updateHistory: {
    type: Boolean,
    default: false,
  },
  ticketId: {
    type: [Number, String],
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:selectedContactId', 'change']);

const store = useStore();
const contactsFromStore = useMapGetter('contacts/getContactsList');
const meta = useMapGetter('contacts/getMeta');
const isLoadingContacts = ref(false);
const isWaitingForDebounce = ref(false);
const currentPage = ref(1);
const searchQuery = ref('');
const multiselectRef = ref(null);
const accumulatedContacts = ref([]);
const DEBOUNCE_DELAY = 500;

const transformContact = contact => ({
  id: contact.id,
  name:
    contact.name || contact.email || contact.phoneNumber || 'Contato sem nome',
  email: contact.email,
  phoneNumber: contact.phoneNumber,
  thumbnail: contact.thumbnail,
  availability_status: contact.availabilityStatus,
});

const availableContacts = computed(() => {
  let contacts = [];

  if (accumulatedContacts.value.length > 0) {
    contacts = accumulatedContacts.value;
  } else if (contactsFromStore.value && contactsFromStore.value.length > 0) {
    contacts = contactsFromStore.value.map(transformContact);
  } else if (props.contacts && props.contacts.length > 0) {
    contacts = props.contacts.map(transformContact);
  }

  if (
    isWaitingForDebounce.value &&
    searchQuery.value &&
    searchQuery.value.trim() !== ''
  ) {
    contacts = [
      {
        id: '__loading__',
        name: 'Buscando...',
        email: '',
        phoneNumber: '',
        thumbnail: '',
        availability_status: null,
        isLoading: true,
      },
      ...contacts,
    ];
  }

  if (
    props.selectedContactId &&
    !contacts.some(c => c.id === props.selectedContactId)
  ) {
    if (
      props.initialContact &&
      props.initialContact.id === props.selectedContactId
    ) {
      const transformedContact = transformContact({
        id: props.initialContact.id,
        name: props.initialContact.name,
        email: props.initialContact.email,
        phoneNumber:
          props.initialContact.phone || props.initialContact.phone_number,
        thumbnail: props.initialContact.thumbnail,
        availabilityStatus:
          props.initialContact.availability_status ||
          props.initialContact.availabilityStatus,
      });
      contacts = [transformedContact, ...contacts];
    } else {
      const contactFromStore = store.getters['contacts/getContact'](
        props.selectedContactId
      );
      if (contactFromStore && contactFromStore.id) {
        const transformedContact = transformContact({
          id: contactFromStore.id,
          name: contactFromStore.name,
          email: contactFromStore.email,
          phoneNumber: contactFromStore.phone_number,
          thumbnail: contactFromStore.thumbnail,
          availabilityStatus: contactFromStore.availability_status,
        });
        contacts = [transformedContact, ...contacts];
      }
    }
  }

  return contacts;
});

const selectedContact = computed(() => {
  if (!props.selectedContactId) {
    return {};
  }

  const foundContact = availableContacts.value.find(
    c => c.id === props.selectedContactId
  );
  if (foundContact) {
    return foundContact;
  }

  const contactFromStore = store.getters['contacts/getContact'](
    props.selectedContactId
  );
  if (contactFromStore && contactFromStore.id) {
    return {
      id: contactFromStore.id,
      name:
        contactFromStore.name || contactFromStore.email || 'Contato sem nome',
      name:
        contactFromStore.name || contactFromStore.email || 'Contato sem nome',
      email: contactFromStore.email,
      phoneNumber: contactFromStore.phone_number,
      thumbnail: contactFromStore.thumbnail,
      availability_status: contactFromStore.availability_status,
      availability_status: contactFromStore.availability_status,
    };
  }

  return {
    id: props.selectedContactId,
    name: 'Carregando...',
    email: '',
    phoneNumber: '',
    thumbnail: '',
    availability_status: null,
  };
});

const fetchContacts = async (page = 1, reset = false) => {
  const selectedContactToPreserve =
    reset && props.selectedContactId
      ? accumulatedContacts.value.find(c => c.id === props.selectedContactId) ||
        (props.initialContact &&
        props.initialContact.id === props.selectedContactId
          ? transformContact({
              id: props.initialContact.id,
              name: props.initialContact.name,
              email: props.initialContact.email,
              phoneNumber:
                props.initialContact.phone || props.initialContact.phone_number,
              thumbnail: props.initialContact.thumbnail,
              availabilityStatus:
                props.initialContact.availability_status ||
                props.initialContact.availabilityStatus,
            })
          : null)
      : null;

  // Preserve contacts from props when resetting
  const propsContactsToPreserve =
    reset && props.contacts && props.contacts.length > 0
      ? props.contacts.map(transformContact)
      : [];

  if (reset) {
    currentPage.value = 1;
    accumulatedContacts.value = [];
  }

  isLoadingContacts.value = true;
  try {
    await store.dispatch('contacts/clearContactFilters');
    await store.dispatch('contacts/get', {
      page,
      sortAttr: 'name',
    });

    await nextTick();

    if (contactsFromStore.value && contactsFromStore.value.length > 0) {
      const newContacts = contactsFromStore.value.map(transformContact);
      if (reset) {
        // Merge props contacts with fetched contacts, avoiding duplicates
        const propsContactIds = new Set(propsContactsToPreserve.map(c => c.id));
        const uniqueNewContacts = newContacts.filter(
          c => !propsContactIds.has(c.id)
        );
        accumulatedContacts.value = [
          ...propsContactsToPreserve,
          ...uniqueNewContacts,
        ];

        // Ensure selected contact is preserved if not already in the list
        if (
          selectedContactToPreserve &&
          !accumulatedContacts.value.some(
            c => c.id === selectedContactToPreserve.id
          )
        ) {
          accumulatedContacts.value = [
            selectedContactToPreserve,
            ...accumulatedContacts.value,
          ];
        }
      } else {
        const existingIds = new Set(accumulatedContacts.value.map(c => c.id));
        const uniqueNewContacts = newContacts.filter(
          c => !existingIds.has(c.id)
        );
        accumulatedContacts.value = [
          ...accumulatedContacts.value,
          ...uniqueNewContacts,
        ];
      }
    } else if (reset) {
      // If no contacts from store, preserve props contacts and selected contact
      accumulatedContacts.value = [...propsContactsToPreserve];
      if (
        selectedContactToPreserve &&
        !accumulatedContacts.value.some(
          c => c.id === selectedContactToPreserve.id
        )
      ) {
        accumulatedContacts.value = [
          selectedContactToPreserve,
          ...accumulatedContacts.value,
        ];
      }
    }

    currentPage.value = page;
  } catch (error) {
    // Error loading contacts
  } finally {
    isLoadingContacts.value = false;
  }
};

const searchContacts = debounce(async (query, page = 1) => {
  isWaitingForDebounce.value = false;

  if (!query || query.trim() === '') {
    await fetchContacts(1, true);
    return;
  }

  const selectedContactToPreserve =
    page === 1 && props.selectedContactId
      ? accumulatedContacts.value.find(c => c.id === props.selectedContactId)
      : null;

  // Preserve contacts from props when resetting search
  const propsContactsToPreserve =
    page === 1 && props.contacts && props.contacts.length > 0
      ? props.contacts.map(transformContact)
      : [];

  const reset = page === 1;
  if (reset) {
    accumulatedContacts.value = [];
  }

  isLoadingContacts.value = true;
  try {
    await store.dispatch('contacts/clearContactFilters');
    await store.dispatch('contacts/search', {
      page,
      sortAttr: 'name',
      search: encodeURIComponent(query.trim()),
    });

    if (contactsFromStore.value && contactsFromStore.value.length > 0) {
      const newContacts = contactsFromStore.value.map(transformContact);
      if (reset) {
        // Merge props contacts with search results, avoiding duplicates
        const propsContactIds = new Set(propsContactsToPreserve.map(c => c.id));
        const uniqueNewContacts = newContacts.filter(
          c => !propsContactIds.has(c.id)
        );
        accumulatedContacts.value = [
          ...propsContactsToPreserve,
          ...uniqueNewContacts,
        ];

        // Ensure selected contact is preserved if not already in the list
        if (
          selectedContactToPreserve &&
          !accumulatedContacts.value.some(
            c => c.id === selectedContactToPreserve.id
          )
        ) {
          accumulatedContacts.value = [
            selectedContactToPreserve,
            ...accumulatedContacts.value,
          ];
        }
      } else {
        const existingIds = new Set(accumulatedContacts.value.map(c => c.id));
        const uniqueNewContacts = newContacts.filter(
          c => !existingIds.has(c.id)
        );
        accumulatedContacts.value = [
          ...accumulatedContacts.value,
          ...uniqueNewContacts,
        ];
      }
    } else if (reset) {
      // If no search results, preserve props contacts and selected contact
      accumulatedContacts.value = [...propsContactsToPreserve];
      if (
        selectedContactToPreserve &&
        !accumulatedContacts.value.some(
          c => c.id === selectedContactToPreserve.id
        )
      ) {
        accumulatedContacts.value = [
          selectedContactToPreserve,
          ...accumulatedContacts.value,
        ];
      }
    }

    currentPage.value = page;
  } catch (error) {
    // Error searching contacts
  } finally {
    isLoadingContacts.value = false;
    isWaitingForDebounce.value = false;
  }
}, DEBOUNCE_DELAY);

const loadMoreContacts = async () => {
  if (isLoadingContacts.value) return;

  const nextPage = currentPage.value + 1;
  const totalPages = meta.value?.count
    ? Math.ceil(meta.value.count / (meta.value.per_page || 15))
    : 1;

  if (nextPage > totalPages) return;

  if (searchQuery.value && searchQuery.value.trim() !== '') {
    await searchContacts(searchQuery.value, nextPage);
  } else {
    await fetchContacts(nextPage, false);
  }
};

const ensureSelectedContactAvailable = async () => {
  if (!props.selectedContactId) return;

  try {
    await store.dispatch('contacts/show', { id: props.selectedContactId });
    const contactFromStore = store.getters['contacts/getContact'](
      props.selectedContactId
    );
    if (contactFromStore && contactFromStore.id) {
      const transformedContact = transformContact({
        id: contactFromStore.id,
        name: contactFromStore.name,
        email: contactFromStore.email,
        phoneNumber: contactFromStore.phone_number,
        thumbnail: contactFromStore.thumbnail,
        availabilityStatus: contactFromStore.availability_status,
      });
      if (
        !accumulatedContacts.value.some(c => c.id === transformedContact.id)
      ) {
        accumulatedContacts.value = [
          transformedContact,
          ...accumulatedContacts.value,
        ];
      }
    }
  } catch (error) {
    // Error fetching selected contact
  }
};

watch(
  contactsFromStore,
  newContacts => {
    if (newContacts && newContacts.length > 0 && currentPage.value > 1) {
      const newTransformedContacts = newContacts.map(transformContact);
      const existingIds = new Set(accumulatedContacts.value.map(c => c.id));
      const uniqueNewContacts = newTransformedContacts.filter(
        c => !existingIds.has(c.id)
      );
      if (uniqueNewContacts.length > 0) {
        accumulatedContacts.value = [
          ...accumulatedContacts.value,
          ...uniqueNewContacts,
        ];
      }
    }
  },
  { deep: true }
);

watch(
  () => props.selectedContactId,
  async (newContactId, oldContactId) => {
    if (newContactId && newContactId !== oldContactId) {
      try {
        await ensureSelectedContactAvailable();
      } catch (error) {
        // Error in selectedContactId watcher
      }
    }
  },
  { immediate: true }
);

const handleSearch = query => {
  searchQuery.value = query;

  if (query && query.trim() !== '') {
    isWaitingForDebounce.value = true;
  } else {
    isWaitingForDebounce.value = false;
  }
  searchContacts(query, 1);
};

const handleScroll = event => {
  if (event.nearBottom) {
    loadMoreContacts();
  }
};

onMounted(async () => {
  // Initialize accumulatedContacts with props.contacts to preserve them
  if (props.contacts && props.contacts.length > 0) {
    const transformedContacts = props.contacts.map(transformContact);
    accumulatedContacts.value = transformedContacts;
  }

  if (props.initialContact && props.initialContact.id) {
    const transformedContact = transformContact({
      id: props.initialContact.id,
      name: props.initialContact.name,
      email: props.initialContact.email,
      phoneNumber:
        props.initialContact.phone || props.initialContact.phone_number,
      thumbnail: props.initialContact.thumbnail,
      availabilityStatus:
        props.initialContact.availability_status ||
        props.initialContact.availabilityStatus,
    });
    if (!accumulatedContacts.value.some(c => c.id === transformedContact.id)) {
      accumulatedContacts.value = [
        transformedContact,
        ...accumulatedContacts.value,
      ];
    }
    store.commit('contacts/SET_CONTACT_ITEM', props.initialContact);
  }

  await ensureSelectedContactAvailable();

  if (!props.contacts || props.contacts.length <= 1) {
    await fetchContacts(1, true);
  }
});

const handleRemoveContact = async () => {
  if (!props.updateHistory || !props.ticketId) return;
  try {
    const response = await store.dispatch('tickets/removeContact', {
      id: props.ticketId,
    });

    if (response) {
      emit('update:selectedContactId', null);
      emit('change', null);
    }

      // Reload ticket to ensure status label is updated after automatic status change
      await store.dispatch('tickets/getTicket', props.ticketId);
      await store.dispatch('tickets/getTicketHistory', props.ticketId);
    } catch (error) {
      // Error removing contact
    }

  try {
    if (!availableContacts.value || availableContacts.value.length <= 1) {
      await fetchContacts(1, true);
    }
  } catch (error) {
    // Error loading contacts
  }
};

const handleContactChange = async newContact => {
  if (newContact.isLoading || newContact.id === '__loading__') {
    return;
  }

  if (newContact.id === props.selectedContactId) {
    emit('update:selectedContactId', null);
    emit('change', null);

    if (props.updateHistory && props.ticketId) {
      await handleRemoveContact();
    }
    return;
  }

  emit('update:selectedContactId', newContact.id);
  emit('change', newContact);

  if (props.updateHistory && props.ticketId) {
    try {
      const response = await store.dispatch('tickets/changeContact', {
        id: props.ticketId,
        newContact: newContact.id,
      });

      if (response && response.contact_name) {
        store.commit('tickets/UPDATE_TICKET_CONTACT', {
          id: props.ticketId,
          contact_name: response.contact_name,
          contact_id: response.contact_id,
        });
      }

      // Reload ticket to ensure status label is updated after automatic status change
      await store.dispatch('tickets/getTicket', props.ticketId);
      await store.dispatch('tickets/getTicketHistory', props.ticketId);
    } catch (error) {
      emit('update:selectedContactId', props.selectedContactId);
    }
  }
};
</script>

<template>
  <div>
    <div @click.prevent>
      <MultiselectDropdown
        ref="multiselectRef"
        :options="availableContacts"
        :selected-item="selectedContact"
        multiselector-title="Selecionar Contato"
        multiselector-placeholder="Selecionar Contato"
        input-placeholder="Buscar contato..."
        no-search-result="Nenhum contato encontrado"
        :has-thumbnail="false"
        :disabled="disabled"
        @select="handleContactChange"
        @search="handleSearch"
        @scroll="handleScroll"
      />
    </div>
  </div>
</template>
